/**
 * DestinationNode — Custom ReactFlow node for the export destination.
 *
 * Shown on canvas after the user configures an export destination.
 * Purple-themed with connector-type icon and status lifecycle.
 *
 * Status lifecycle:
 *   pending  → dashed border, "Ready to export"
 *   exporting → animated pulse, "Exporting..."
 *   exported → green border + checkmark, "50,000 rows exported"
 *   failed   → red border + error icon, error message
 *
 * Visual spec:
 * - Width 240px, white background, purple left border
 * - Header: connector icon + connector name + status badge
 * - Description: target table/path + row count
 * - Top Handle only (true terminal — no bottom handle)
 */
import React from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Box, Button, Tooltip, Typography, alpha } from '@mui/material';
import {
  Storage as DatabaseIcon,
  CloudUpload as CloudIcon,
  Check as CheckIcon,
  ErrorOutline as ErrorIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { ReadinessChip } from './readiness/ReadinessChip';
import type { ReadinessStatus } from '../../types/readiness';

export interface DestinationNodeData {
  title: string;
  connectorType: string;
  targetTable?: string;
  status: 'pending' | 'exporting' | 'exported' | 'failed';
  rowsWritten?: number;
  exportedAt?: string;
  error?: string;
  /** Callback to start the export. */
  onStartExport?: () => void;
  /** Readiness rollout — populated from a /readiness fetch by the
   *  workspace page. When status is not_ready, "Start export" is
   *  disabled and the chip surfaces the count. */
  readinessStatus?: ReadinessStatus;
  readinessFindingCount?: number;
}

/** Pick an icon based on connector type. */
function connectorIcon(type: string): React.ReactElement {
  if (['s3', 'gcs', 'filesystem', 'filesystem_destination'].includes(type)) {
    return <CloudIcon sx={{ fontSize: 16, color: '#7c3aed' }} />;
  }
  return <DatabaseIcon sx={{ fontSize: 16, color: '#7c3aed' }} />;
}

const DestinationNode: React.FC<NodeProps<DestinationNodeData>> = ({
  data,
  selected,
  targetPosition,
}) => {
  const targetHandlePos = targetPosition ?? Position.Top;
  const { batchId } = useParams<{ batchId: string }>();
  const { exportProgressMap } = useNotifications();
  const exportProgress = batchId ? exportProgressMap[batchId] : undefined;

  const isPending = data.status === 'pending';
  const isExporting = data.status === 'exporting';
  const isExported = data.status === 'exported';
  const isFailed = data.status === 'failed';

  // Accent shifts based on status
  const accentColor = isFailed ? '#dc2626' : isExported ? '#059669' : '#7c3aed';
  const borderDefault = isPending
    ? `1.5px dashed ${alpha('#7c3aed', 0.4)}`
    : isFailed
      ? '1.5px solid #fecaca'
      : isExported
        ? '1.5px solid #d1fae5'
        : '1.5px solid #ede9fe';

  return (
    <Box
      sx={{
        width: 240,
        background: '#fff',
        borderRadius: 0.5,
        border: selected ? `2px solid ${accentColor}` : borderDefault,
        borderLeft: `4px solid ${accentColor}`,
        boxShadow: selected
          ? `0 0 0 3px ${alpha(accentColor, 0.15)}`
          : '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        cursor: 'pointer',
        ...(isExporting && {
          animation: 'destinationPulse 2s ease-in-out infinite',
          '@keyframes destinationPulse': {
            '0%, 100%': { boxShadow: `0 0 0 0px ${alpha('#7c3aed', 0.1)}` },
            '50%': { boxShadow: `0 0 0 4px ${alpha('#7c3aed', 0.15)}` },
          },
        }),
      }}
    >
      {/* Target handle */}
      <Handle
        type="target"
        position={targetHandlePos}
        style={{
          background: accentColor,
          width: 8,
          height: 8,
          border: `2px solid ${alpha(accentColor, 0.3)}`,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: alpha('#7c3aed', 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {connectorIcon(data.connectorType)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={data.title} placement="top" arrow disableInteractive>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                fontSize: 12,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {data.title}
            </Typography>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{ fontSize: 10, color: '#6b7280', fontVariant: 'small-caps', letterSpacing: 0.5 }}
          >
            DESTINATION
          </Typography>
        </Box>
        {/* Readiness chip — when populated, surfaces blocker count
            so the user knows the export will be refused. */}
        {data.readinessStatus && (
          <Box sx={{ flexShrink: 0, mr: 0.5, transform: 'scale(0.8)', transformOrigin: 'right center' }}>
            <ReadinessChip
              state={data.readinessStatus}
              count={data.readinessFindingCount ?? 0}
              size="small"
            />
          </Box>
        )}
        {/* Status badge */}
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: accentColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            ...(isExporting && {
              animation: 'spinBadge 1.5s linear infinite',
              '@keyframes spinBadge': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }),
          }}
        >
          {isExported && <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />}
          {isFailed && <ErrorIcon sx={{ fontSize: 14, color: '#fff' }} />}
          {isPending && <PendingIcon sx={{ fontSize: 14, color: '#fff' }} />}
          {isExporting && <PendingIcon sx={{ fontSize: 14, color: '#fff' }} />}
        </Box>
      </Box>

      {/* Description / Status */}
      <Box sx={{ px: 1.5, pb: 1 }}>
        {isPending && (
          <>
            <Typography variant="caption" sx={{ fontSize: 11, color: '#94a3b8', display: 'block' }}>
              Ready to export
              {data.targetTable && ` → ${data.targetTable}`}
            </Typography>
            {data.onStartExport && (
              <Tooltip
                title={
                  data.readinessStatus === 'not_ready'
                    ? 'Resolve the issues in the Readiness panel before exporting.'
                    : ''
                }
                arrow
              >
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={data.readinessStatus === 'not_ready'}
                    onClick={(e) => {
                      e.stopPropagation();
                      data.onStartExport!();
                    }}
                    sx={{
                      mt: 0.5,
                      fontSize: 10,
                      textTransform: 'none',
                      bgcolor: '#7c3aed',
                      '&:hover': { bgcolor: '#6d28d9' },
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.25,
                      minHeight: 0,
                      lineHeight: 1.4,
                      boxShadow: 'none',
                    }}
                  >
                    Start Export
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        )}
        {isExporting && (
          <Typography variant="caption" sx={{ fontSize: 11, color: '#7c3aed', display: 'block' }}>
            {exportProgress && exportProgress.tablesTotal > 0
              ? `Exporting… ${exportProgress.tablesCompleted}/${exportProgress.tablesTotal} tables`
              : `Exporting${data.targetTable ? ` to ${data.targetTable}` : ''}…`}
          </Typography>
        )}
        {isExported && (
          <>
            <Typography variant="caption" sx={{ fontSize: 11, color: '#059669', display: 'block', fontWeight: 600 }}>
              {data.rowsWritten != null
                ? `${data.rowsWritten.toLocaleString()} rows exported`
                : 'Export complete'}
            </Typography>
            {data.targetTable && (
              <Typography variant="caption" sx={{ fontSize: 10, color: '#94a3b8', display: 'block' }}>
                → {data.targetTable}
              </Typography>
            )}
          </>
        )}
        {isFailed && (
          <Tooltip title={data.error || 'Export failed'} placement="bottom" arrow disableInteractive>
            <Typography
              variant="caption"
              sx={{
                fontSize: 11,
                color: '#dc2626',
                display: 'block',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {data.error || 'Export failed'}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* No bottom handle — true terminal node */}
    </Box>
  );
};

export default DestinationNode;
